/**
 * 基础配置 - 坐席管理 接口定义
 */
import { DEFAULT_PAGE_SIZE } from '@/constants/api';
import { apiGet, apiPost, apiPut, apiRequest, apiDownload } from '@/utils/request';
import { parseListResponse } from '@/utils/apiResponse';
import type { AgentImportResult } from '@/types/agents';
import type { ConditionPayload } from '@/types/conditionBuilder';

/** 坐席分页列表 Query 参数 */
export interface AgentsListParams {
  page?: number;
  page_size?: number;
  agent_group_id?: number;
  status?: 0 | 1;
  keyword?: string;
  role_id?: string | number;
  phone?: string;
}

/** 坐席列表项 */
export interface AgentListItem {
  id?: number;
  nickname?: string;
  phone?: string;
  username?: string;
  no?: string | null;
  status?: number;
  is_online?: number;
  agent_group_ids?: number[];
  agent_groups?: { id?: number; name?: string }[];
  /** 角色 ID（统一字段，替代原 app_role_id / custom_role_id） */
  role_id?: number | null;
  custom_role?: { id?: number; name?: string } | null;
  role_code?: string | null;
  role_name?: string | null;
  avatar?: string | null;
  remark?: string | null;
  created_at?: string;
  updated_at?: string;
  [key: string]: unknown;
}

/** 坐席分页响应 */
export interface AgentsPaginatedResponse {
  list: AgentListItem[];
  page: number;
  page_size: number;
  total: number;
}

/** 分组树节点类型 */
export interface AgentGroupTreeNode {
  id: number;
  name: string;
  agents_count: number;
  parent_id?: number | null;
  level?: number;
  description?: string | null;
  children?: AgentGroupTreeNode[];
}

/** 渠道分配 - 可用账号 */
export interface ChannelDistributionAccount {
  id: number;
  name: string;
  alias?: string;
  external_id?: string;
  bound_group_id?: number | null;
  bound_group_name?: string | null;
  [key: string]: unknown;
}

/** 渠道分配 - 渠道项 */
export interface ChannelDistributionItem {
  channel_id: number;
  channel_key: string;
  channel_name: string;
  auto_include: boolean;
  auto_include_locked: boolean;
  auto_include_locked_by_group: string | null;
  available_accounts: ChannelDistributionAccount[];
  selected_account_ids: number[];
  total_accounts: number;
}

/** 渠道分配详情响应 */
export interface ChannelDistributionResponse {
  group_id: number;
  channels: ChannelDistributionItem[];
}

/** 用户分配配置详情响应 */
export interface AllocationConfigResponse {
  type: 'opening' | 'customer' | 'condition';
  mechanism?: 'online_only' | 'online_and_offline';
  time_slots: Array<{
    start_time?: string;
    end_time?: string;
    method?: 'sequential' | 'weight';
    agents: Array<{ agent_id: number; weight: number }>;
  }>;
  /** 自定义分配（type=condition）的命中条件，格式与触发条件一致：[[且...], [或...]] */
  conditions?: ConditionPayload;
}

export const agentsApi = {
  /** 坐席分页列表：page、page_size、agent_group_id、status、keyword、role_id、phone */
  list: (params: AgentsListParams & Record<string, unknown> = {}) =>
    apiGet('/agents', {
      page: params.page ?? 1,
      page_size: params.page_size ?? DEFAULT_PAGE_SIZE,
      ...(params.agent_group_id != null && { agent_group_id: params.agent_group_id }),
      ...(params.status != null && { status: params.status }),
      ...(params.keyword?.trim() && { keyword: params.keyword.trim() }),
      ...(params.role_id != null && params.role_id !== '' && { role_id: params.role_id }),
      ...(params.phone?.trim() && { phone: params.phone.trim() }),
      ...(Array.isArray(params.ids) && params.ids.length > 0 && { ids: params.ids.join(',') }),
    }).then((d) => parseAgentsPaginatedResponse(d)),
  show: (id: number) => apiGet(`/agents/${id}`),
  /** 新建坐席：nickname、agent_group_ids、phone 必填；username、password、role_id、remark 可选 */
  create: (data: {
    nickname: string;
    agent_group_ids: number[];
    phone: string;
    username?: string;
    password?: string;
    role_id?: number | null;
    remark?: string | null;
  }) => {
    const payload: Record<string, unknown> = {
      nickname: String(data.nickname).trim().slice(0, 64),
      agent_group_ids: data.agent_group_ids.map(String),
      phone: String(data.phone).trim().slice(0, 20),
    };
    if (data.username != null && data.username !== '')
      payload.username = String(data.username).trim();
    if (data.password != null && data.password !== '') payload.password = data.password;
    if (data.role_id != null) payload.role_id = data.role_id;
    if (data.remark != null && data.remark !== '')
      payload.remark = String(data.remark).slice(0, 500);
    return apiPost('/agents', payload);
  },
  /** 更新坐席 PUT /agents/{id}：nickname、phone、remark、agent_group_ids、role_id、password、status 均为可选 */
  update: (
    id: number,
    data: {
      nickname?: string;
      phone?: string;
      remark?: string | null;
      agent_group_ids?: number[];
      role_id?: number | null;
      password?: string;
      status?: 0 | 1;
    } & Record<string, unknown>,
  ) => {
    const payload: Record<string, unknown> = {};
    if (data.nickname !== undefined) payload.nickname = String(data.nickname).trim().slice(0, 64);
    if (data.phone !== undefined) payload.phone = String(data.phone).trim().slice(0, 20);
    if (data.remark !== undefined)
      payload.remark = data.remark == null ? null : String(data.remark).slice(0, 500);
    if (data.agent_group_ids !== undefined) payload.agent_group_ids = data.agent_group_ids;
    if (data.role_id !== undefined) payload.role_id = data.role_id;
    if (data.password != null && data.password !== '') {
      const pwd = String(data.password);
      if (pwd.length >= 6 && pwd.length <= 64) payload.password = pwd;
    }
    if (data.status !== undefined) payload.status = data.status;
    return apiPut(`/agents/${id}`, payload);
  },
  /**
   * 消息声音提醒开关：POST /api/agents/sound-notify
   *
   * 不传 agent_id 即改当前登录坐席自己（无需坐席管理权限）；传了则按「编辑坐席」同样的权限校验。
   * 两个开关至少传一个，未传的保持原值。
   */
  soundNotify: (data: {
    agent_id?: number;
    /** 公海消息声音提醒：未分配给自己的会话来新消息时是否响 */
    public_msg_sound?: 0 | 1;
    /** 个人消息声音提醒：分配给自己的会话来新消息时是否响 */
    personal_msg_sound?: 0 | 1;
  }) => apiPost('/agents/sound-notify', data),
  /** 删除坐席：DELETE /api/agents/{id} */
  delete: (id: number) => apiRequest(`/agents/${id}`, { method: 'DELETE' }),
  /** 批量调整坐席状态：POST /api/agents/batch-status，agent_ids、status 必填 */
  queryBatchStatus: (agentIds: number[], status: 0 | 1) =>
    apiPost('/agents/batch-status', { agent_ids: agentIds, status }),
  /** 批量调整坐席分组：POST /api/agents/batch-groups，agent_ids、agent_group_ids 必填且至少 1 项 */
  queryBatchGroups: (agentIds: number[], agentGroupIds: number[]) =>
    apiPost('/agents/batch-groups', { agent_ids: agentIds, agent_group_ids: agentGroupIds }),
  /** 下载坐席导入模板：GET /api/agents/download-template */
  downloadTemplate: () =>
    apiDownload('/agents/download-template', { filename: '坐席分组导入.xlsx' }),
  /** 导入坐席：POST /api/agents/import，multipart/form-data，file 必填 */
  import: (data: FormData) =>
    apiRequest<AgentImportResult>('/agents/import', { method: 'POST', data }),
};

/** 坐席远程分页选项（供条件构建器"历史接待客服"/"当前接待客服"使用） */
export async function fetchAgentRemoteOptions(params: {
  page: number;
  pageSize: number;
  keyword: string;
  ids?: (string | number)[];
}): Promise<{ list: Record<string, any>[]; total: number }> {
  const res = await agentsApi.list({
    page: params.page,
    page_size: params.pageSize,
    keyword: params.keyword || undefined,
    ids: params.ids && params.ids.length > 0 ? params.ids : undefined,
  });
  return {
    list: res.list.map((a) => ({ id: a.id, name: a.nickname })),
    total: res.total,
  };
}

export const agentGroupsApi = {
  list: () => apiGet('/agent-groups').then((d) => parseListResponse(d)),
  /** 获取坐席分组树：Level 1 分组 -> Level 2 子分组，每节点含 agents_count */
  queryTree: (): Promise<AgentGroupTreeNode[]> =>
    apiGet('/agent-groups').then((d) => buildAgentGroupTree(d)),
  show: (id: number) => apiGet(`/agent-groups/${id}`),
  /** 新建坐席分组：name、parent_id、description；level 由后端按 parent_id 判定，前端不传 */
  create: (data: { name: string; parent_id?: number | null; description?: string | null }) => {
    const payload: Record<string, unknown> = { name: data.name.trim() };
    if (data.parent_id !== undefined) payload.parent_id = data.parent_id;
    if (data.description != null && data.description !== '') payload.description = data.description;
    return apiPost('/agent-groups', payload);
  },
  /** 编辑坐席分组：PUT /agent-groups/{id}，name≤64、description≤500，或 channel_configs 等 */
  update: (id: number, data: Record<string, unknown>) => {
    const payload = { ...data };
    if (payload.name != null) payload.name = String(payload.name).trim().slice(0, 64);
    if (payload.description !== undefined)
      payload.description =
        payload.description == null ? null : String(payload.description).slice(0, 500);
    return apiPut(`/agent-groups/${id}`, payload);
  },
  /** 删除坐席分组：DELETE /agent-groups/{id} */
  delete: (id: number) => apiRequest(`/agent-groups/${id}`, { method: 'DELETE' }),
  agentsInGroup: (id: number) =>
    apiGet(`/agent-groups/${id}/agents`).then((d) => parseListResponse(d)),
  /** 渠道分配详情：GET /api/agent-groups/{id}/channel-distribution */
  queryChannelDistribution: (id: number) =>
    apiGet(`/agent-groups/${id}/channel-distribution`).then(
      (d) =>
        (d as { data?: ChannelDistributionResponse })?.data ?? (d as ChannelDistributionResponse),
    ),
  /** 保存渠道分配配置：PUT /api/agent-groups/{id}/channel-distribution，channels 为 channel_key -> { auto_include, account_ids }，auto_include=true 时 account_ids 传空数组 */
  updateChannelDistribution: (
    id: number,
    channels: Record<string, { auto_include: boolean; account_ids: string[] }>,
  ) => apiPut(`/agent-groups/${id}/channel-distribution`, { channels }),
  /** 获取用户分配配置详情：GET /api/agent-groups/{id}/allocation-config，返回可能为 { allocation_config } 或直接为配置对象 */
  queryAllocationConfig: (id: number) =>
    apiGet(`/agent-groups/${id}/allocation-config`).then((d) => {
      const raw = (d as { data?: AllocationConfigResponse })?.data ?? d;
      const cfg = (raw as { allocation_config?: AllocationConfigResponse })?.allocation_config;
      return (cfg ?? raw) as AllocationConfigResponse;
    }),
  /**
   * 保存用户分配配置：PUT /api/agent-groups/{id}/allocation-config
   * type=opening|customer|condition，mechanism 仅 type=customer|condition 有效，
   * conditions 仅 type=condition 有效，time_slots 可为空数组（用户删光时间段时）
   */
  updateAllocationConfig: (
    id: number,
    data: {
      type: 'opening' | 'customer' | 'condition';
      mechanism?: 'online_only' | 'online_and_offline';
      time_slots: Array<{
        start_time?: string;
        end_time?: string;
        method?: 'sequential' | 'weight';
        agents: Array<{ agent_id: number; weight: number }>;
      }>;
      conditions?: ConditionPayload;
      /** AI 接待：0=分配后继续接待，1=分配后停止接待 */
      ai_stop?: number;
    },
  ) => apiPut(`/agent-groups/${id}/allocation-config`, data),
};

/** 规范化单个节点 */
function normalizeNode(x: Record<string, unknown>): AgentGroupTreeNode {
  const rawChildren = x.children;
  const children: AgentGroupTreeNode[] = Array.isArray(rawChildren)
    ? (rawChildren as Record<string, unknown>[]).map(normalizeNode)
    : [];
  return {
    id: x.id as number,
    name: String(x.name ?? ''),
    agents_count: Number(x.agents_count ?? 0),
    parent_id: x.parent_id as number | null | undefined,
    level: x.level as number | undefined,
    description: x.description as string | null | undefined,
    children: children.length ? children : undefined,
  };
}

/** 从接口数据构建分组树：接口已是嵌套树则直接规范化，扁平列表则按 parent_id 组树 */
function buildAgentGroupTree(d: unknown): AgentGroupTreeNode[] {
  const raw =
    (d as { list?: unknown; data?: unknown })?.list ?? (d as { data?: unknown })?.data ?? d;
  const list = Array.isArray(raw)
    ? raw
    : raw && typeof raw === 'object' && !Array.isArray(raw)
      ? [raw]
      : [];
  const first = list[0] as Record<string, unknown> | undefined;
  if (first && Array.isArray(first.children)) {
    return list.map((x: Record<string, unknown>) => normalizeNode(x));
  }
  const items = list.map((x: Record<string, unknown>) => ({
    id: x.id as number,
    name: String(x.name ?? ''),
    agents_count: Number(x.agents_count ?? 0),
    parent_id: x.parent_id as number | null | undefined,
    level: x.level as number | undefined,
    description: x.description as string | null | undefined,
    children: [] as AgentGroupTreeNode[],
  }));
  const byId = new Map<number, AgentGroupTreeNode>();
  items.forEach((n) => byId.set(n.id, n));
  const roots: AgentGroupTreeNode[] = [];
  items.forEach((n) => {
    const pid = n.parent_id;
    if (pid != null && byId.has(pid)) {
      const parent = byId.get(pid)!;
      if (!parent.children) parent.children = [];
      parent.children.push(n);
    } else {
      roots.push(n);
    }
  });
  return roots;
}

/** 解析坐席分页响应 */
function parseAgentsPaginatedResponse(d: unknown): AgentsPaginatedResponse {
  const obj = d as { list?: unknown; page?: number; page_size?: number; total?: number } | null;
  return {
    list: Array.isArray(obj?.list) ? (obj.list as AgentListItem[]) : [],
    page: typeof obj?.page === 'number' ? obj.page : 1,
    page_size: typeof obj?.page_size === 'number' ? obj.page_size : DEFAULT_PAGE_SIZE,
    total: typeof obj?.total === 'number' ? obj.total : 0,
  };
}

/** 可选角色项：GET /api/agents/selectable-roles 返回 data.list 元素 */
export interface SelectableRoleItem {
  id: number;
  name: string;
  role_code?: string;
  role_key?: string;
  type: string;
  [key: string]: unknown;
}

/** 获取可选角色列表：GET /api/agents/selectable-roles，返回 data.list */
export async function querySelectableRoles(): Promise<SelectableRoleItem[]> {
  const d = await apiGet('/agents/selectable-roles');
  const list = (d as { list?: SelectableRoleItem[] })?.list;
  if (Array.isArray(list)) return list;
  return parseListResponse(d) as SelectableRoleItem[];
}
